import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  NOTIFICATION_CATEGORY,
  NOTIFICATION_CATEGORY_LABEL,
  notificationCategorySchema,
  type Notification,
  type NotificationCategoryValue,
} from '@teentack/shared';
import { EmptyState } from '@/components/ui/Feedback';
import { Skeleton } from '@/components/ui/Skeleton';
import { relativeTime } from '@/lib/relative-time';
import {
  useMarkNotificationsRead,
  useNotificationPreferences,
  useNotifications,
  useSaveNotificationPreferences,
} from '@/features/notifications/use-notifications';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Toggle } from '@/components/ui/Toggle';
import { BellIcon } from '@/components/ui/Icons';
import { ErrorNote } from '@/components/Form';
import { presentApiError } from '@/lib/form-errors';

/**
 * The inbox, and the switches that control it. [Teen step 19, Parent steps 3, 19]
 *
 * One page rather than two: preferences only make sense next to the thing they
 * silence, and nobody goes looking for a settings screen until a notification
 * has already annoyed them.
 */

const CATEGORIES = notificationCategorySchema.options;

function NotificationRow({ item }: { item: Notification }) {
  const category = NOTIFICATION_CATEGORY[item.type];

  const inner = (
    <div className="flex items-start gap-3">
      {!item.isRead ? (
        <span aria-hidden="true" className="mt-2 size-2 shrink-0 rounded-full bg-brand-600" />
      ) : (
        <span aria-hidden="true" className="mt-2 size-2 shrink-0" />
      )}
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
          <p className={`text-sm ${item.isRead ? 'font-semibold text-slate-600' : 'font-bold text-ink'}`}>
            {item.title}
          </p>
          <span className="text-xs font-medium text-slate-400">{relativeTime(item.createdAt)}</span>
        </div>
        <p className="mt-1 text-sm leading-relaxed text-slate-500">{item.message}</p>
        <div className="mt-2">
          <Badge tone="neutral">{NOTIFICATION_CATEGORY_LABEL[category]}</Badge>
        </div>
      </div>
    </div>
  );

  return (
    <li className={item.isRead ? 'bg-white' : 'bg-brand-50/40'}>
      {item.link ? (
        <Link to={item.link} className="block px-5 py-4 transition hover:bg-brand-50/70">
          {inner}
        </Link>
      ) : (
        <div className="px-5 py-4">{inner}</div>
      )}
    </li>
  );
}

export function NotificationsPage() {
  const [category, setCategory] = useState<NotificationCategoryValue | ''>('');
  const [unreadOnly, setUnreadOnly] = useState(false);

  const inbox = useNotifications({
    pageSize: 50,
    ...(category ? { category } : {}),
    ...(unreadOnly ? { unreadOnly: true } : {}),
  });
  const markRead = useMarkNotificationsRead();
  const preferences = useNotificationPreferences();
  const savePreferences = useSaveNotificationPreferences();

  const prefs = preferences.data;

  function toggleCategory(value: NotificationCategoryValue): void {
    if (!prefs) return;
    const muted = prefs.mutedCategories.includes(value)
      ? prefs.mutedCategories.filter((entry) => entry !== value)
      : [...prefs.mutedCategories, value];
    savePreferences.mutate({ mutedCategories: muted });
  }

  const unreadOnPage = inbox.data?.items.filter((item) => !item.isRead).length ?? 0;

  return (
    <main className="container-page py-10">
      <div className="mx-auto max-w-3xl">
        <header className="mb-8">
          <p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400">
            Your account
          </p>
          <h1 className="mt-1 text-2xl font-extrabold tracking-tight text-ink sm:text-3xl">
            Notifications
          </h1>
          <p className="mt-2 max-w-prose text-sm leading-relaxed text-slate-500">
            Everything that has happened on your jobs, approvals and payments.
          </p>
        </header>

        {/* Filters */}
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setCategory('')}
            aria-pressed={category === ''}
            className={`rounded-full px-3.5 py-1.5 text-xs font-semibold transition ${
              category === ''
                ? 'bg-ink text-white'
                : 'border border-line bg-white text-slate-600 hover:border-brand-300'
            }`}
          >
            All
          </button>
          {CATEGORIES.map((value) => (
            <button
              key={value}
              type="button"
              onClick={() => setCategory(value)}
              aria-pressed={category === value}
              className={`rounded-full px-3.5 py-1.5 text-xs font-semibold transition ${
                category === value
                  ? 'bg-ink text-white'
                  : 'border border-line bg-white text-slate-600 hover:border-brand-300'
              }`}
            >
              {NOTIFICATION_CATEGORY_LABEL[value]}
            </button>
          ))}

          <label className="ml-auto inline-flex items-center gap-2 text-xs font-semibold text-slate-600">
            <input
              type="checkbox"
              checked={unreadOnly}
              onChange={(event) => setUnreadOnly(event.target.checked)}
              className="size-4 rounded border-slate-300 text-brand-600"
            />
            Unread only
          </label>
        </div>

        {unreadOnPage > 0 && (
          <div className="mb-4 flex justify-end">
            <Button
              variant="outline"
              onClick={() => markRead.mutate(undefined)}
              disabled={markRead.isPending}
            >
              {markRead.isPending ? 'Marking…' : 'Mark all read'}
            </Button>
          </div>
        )}

        {inbox.isError && (
          <div className="mb-4">
            <ErrorNote message={presentApiError(inbox.error, 'Could not load notifications').message} />
          </div>
        )}

        {inbox.isPending && (
          <div className="space-y-3" aria-busy="true">
            <Skeleton className="h-20" />
            <Skeleton className="h-20" />
            <Skeleton className="h-20" />
          </div>
        )}

        {inbox.data?.items.length === 0 && (
          <EmptyState icon={<BellIcon className="size-6" />} title="Nothing here">
            {unreadOnly || category
              ? 'Nothing matches that filter right now.'
              : 'When someone approves a request, applies to your job or releases a payment, it shows up here.'}
          </EmptyState>
        )}

        {inbox.data && inbox.data.items.length > 0 && (
          <ul className="divide-y divide-line overflow-hidden rounded-2xl border border-line shadow-soft">
            {inbox.data.items.map((item) => (
              <NotificationRow key={item.id} item={item} />
            ))}
          </ul>
        )}

        {/* Preferences */}
        <section className="mt-12">
          <h2 className="text-base font-bold text-ink">What you hear about</h2>
          <p className="mt-1 text-sm text-slate-500">
            Turning a category off stops new notifications in it. Anything already here stays.
          </p>

          {prefs && (
            <div className="mt-5 overflow-hidden rounded-2xl border border-line bg-white shadow-soft">
              <ul className="divide-y divide-line">
                {CATEGORIES.map((value) => {
                  const muted = prefs.mutedCategories.includes(value);
                  return (
                    <li key={value} className="px-5">
                      <Toggle
                        label={NOTIFICATION_CATEGORY_LABEL[value]}
                        checked={!muted}
                        disabled={savePreferences.isPending}
                        onChange={() => toggleCategory(value)}
                      />
                    </li>
                  );
                })}
              </ul>

              <div className="border-t border-line bg-canvas px-5">
                <Toggle
                  label="Also email me"
                  description="Sign-in codes and guardian invitations are always emailed."
                  checked={prefs.emailEnabled}
                  disabled={savePreferences.isPending}
                  onChange={(emailEnabled) => savePreferences.mutate({ emailEnabled })}
                />
              </div>
            </div>
          )}

          {savePreferences.isError && (
            <div className="mt-3">
              <ErrorNote
                message={presentApiError(savePreferences.error, 'Could not save that').message}
              />
            </div>
          )}
        </section>
      </div>
    </main>
  );
}
